<template>
  <div>
    <!-- ══════════════════ SUCCESS STATE ══════════════════ -->
    <div v-if="purchaseSuccessVisible" class="reveal relative overflow-hidden rounded-2xl">
      <div class="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <span
            v-for="(piece, i) in confetti"
            :key="i"
            :style="piece"
            class="confetti"
        />
      </div>

      <div class="relative rounded-2xl border border-emerald-200/80 bg-gradient-to-br from-indigo-50 via-white to-emerald-50 p-5 text-center shadow-lg shadow-emerald-500/10 dark:border-emerald-500/30 dark:from-indigo-950/40 dark:via-gray-900 dark:to-emerald-950/30">
        <div class="relative mx-auto mb-3 inline-flex">
          <div class="absolute inset-0 rounded-2xl bg-gradient-to-br from-amber-300 via-pink-400 to-indigo-500 blur-md scale-125 opacity-60 animate-pulse" aria-hidden="true"/>
          <div class="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-300 via-pink-400 to-indigo-500 text-white shadow-lg shadow-pink-500/30 ring-2 ring-white/50 dark:ring-white/20">
            <Crown class="h-7 w-7"/>
          </div>
        </div>

        <h3 class="text-balance text-xl font-extrabold tracking-tight text-gray-900 dark:text-gray-50">
          {{ purchaseSuccessTitle }}
        </h3>
        <p class="mx-auto mt-1.5 max-w-xs text-[13px] leading-relaxed text-gray-600 dark:text-gray-300">
          {{ purchaseSuccessMessage }}
        </p>

        <div class="mt-4 space-y-1.5 rounded-xl bg-white/70 p-3 text-start ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/10">
          <p class="mb-1 text-[10px] font-bold uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
            {{ t('planner.upgrade_page_success_unlocked_label') }}
          </p>
          <div v-for="item in unlockedItems" :key="item" class="flex items-center gap-2">
            <span class="flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
              <Check class="h-3 w-3" stroke-width="3"/>
            </span>
            <span class="text-[13px] font-medium text-gray-700 dark:text-gray-200">{{ item }}</span>
          </div>
        </div>

        <button
            class="upgrade-picker-cta mt-4 w-full rounded-2xl py-3.5 text-[15px] font-bold text-white transition-transform active:scale-[0.98] touch-manipulation"
            type="button"
            @click="$emit('continue')"
        >
          <span class="relative z-10">{{ t('planner.upgrade_page_continue') }}</span>
        </button>
      </div>
    </div>

    <template v-else>
      <div>
      <slot name="intro"/>

      <FoundingOfferNote
          v-if="webFoundingOfferActive"
          :seats-left="foundingSeatsLeft"
          :seat-cap="foundingSeatCap"
          class="mb-2.5 short:mb-2"
      />

      <!-- Native pricing error -->
      <div
          v-if="isNativeMobile && nativePricingError"
          class="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50/90 p-4 dark:border-red-500/35 dark:bg-red-950/25"
      >
        <AlertTriangle class="mt-0.5 h-4.5 w-4.5 shrink-0 text-red-600 dark:text-red-400"/>
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-red-800 dark:text-red-200">
            {{ nativePricingError }}
          </p>
          <button
              :disabled="nativePricingRetryLoading"
              class="mt-3 w-full rounded-lg bg-red-700 py-2.5 text-sm font-semibold text-white transition hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-red-600 dark:hover:bg-red-500"
              type="button"
              @click="retryLoadNativeOfferings"
          >
            {{
              nativePricingRetryLoading
                ? t('planner.upgrade_page_loading_text')
                : t('planner.upgrade_page_native_pricing_retry')
            }}
          </button>
        </div>
      </div>

      <WebPricingState
          v-else-if="webPricingLoading || webPricingFailed"
          :loading="webPricingLoading"
          :failed="webPricingFailed"
          @retry="retryWebPricing"
      />

      <!-- ══════════════════ PLAN CARDS ══════════════════ -->
      <div v-else class="reveal picker-d1 space-y-2.5 short:space-y-2">
        <!-- Annual card -->
        <button
            type="button"
            class="w-full rounded-2xl border-2 p-3.5 short:p-3 text-start transition-all active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            :class="selectedPlan === 'annual'
              ? 'border-indigo-600 bg-indigo-50/80 shadow-md shadow-indigo-500/15 ring-2 ring-indigo-500/20 dark:border-indigo-400 dark:bg-indigo-500/15 dark:ring-indigo-400/20'
              : 'border-gray-200 bg-white/60 hover:border-indigo-300 dark:border-white/15 dark:bg-white/[0.04] dark:hover:border-indigo-500/60'"
            @click="selectedPlan = 'annual'"
        >
          <div class="flex items-start gap-3">
            <span
                class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition-all"
                :class="selectedPlan === 'annual'
                  ? 'bg-indigo-600 dark:bg-indigo-400'
                  : 'border-2 border-gray-300 dark:border-white/30'"
            >
              <Check v-if="selectedPlan === 'annual'" class="h-3 w-3 text-white" stroke-width="3.5"/>
            </span>

            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center gap-2">
                <span class="text-sm font-bold text-gray-900 dark:text-gray-50">
                  {{ t('planner.upgrade_page_plan_annual_title') }}
                </span>
                <span class="rounded-full bg-emerald-700 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white shadow-sm">
                  {{ t('planner.upgrade_page_plan_annual_badge') }}
                </span>
              </div>

              <!-- iOS: billed amount must be the most clear and conspicuous price
                   element (App Store guideline 3.1.2c); monthly equivalent stays
                   visually subordinate beneath it. -->
              <template v-if="isIosPlatform">
                <div class="mt-1.5 short:mt-1">
                  <span class="text-xl font-extrabold text-indigo-700 dark:text-indigo-300">
                    {{ nativeAnnualPriceDisplay }}
                  </span>
                </div>
                <p v-if="nativeAnnualMonthlyEquivalentDisplay" class="mt-0.5 text-[12px] text-gray-500 dark:text-gray-400">
                  {{ nativeAnnualMonthlyEquivalentDisplay }}
                </p>
                <p v-if="annualTrialLine" class="mt-0.5 text-[12px] font-medium text-indigo-600 dark:text-indigo-400">
                  {{ annualTrialLine }}
                </p>
                <p v-if="nativeSavingsPercent !== null" class="mt-1.5 inline-flex items-center gap-1 text-[12px] font-semibold short:mt-1 short:leading-snug text-emerald-700 dark:text-emerald-400">
                  <span class="text-emerald-500">✦</span>
                  {{ t('planner.upgrade_page_store_savings_percent', { percent: nativeSavingsPercent }) }}
                </p>
              </template>

              <!-- Web + Android. The amount that will actually be charged is the
                   prominent one, and the monthly equivalent sits underneath it.
                   This used to be the other way round: a large "€4.99 / month"
                   over a muted "billed annually €59.99", which reads as a
                   monthly price and is the shape of the offer people feel
                   tricked by once the charge lands. iOS already had to do it
                   this way for App Store guideline 3.1.2c; there was never a
                   good reason for web and Android to differ. -->
              <template v-else>
                <div class="mt-1.5 flex items-end justify-between gap-3 short:mt-1">
                  <span class="text-xl font-extrabold text-indigo-700 dark:text-indigo-300">
                    {{ isNativeMobile ? nativeAnnualPriceDisplay : webAnnualPriceDisplay }}
                  </span>
                  <span v-if="webFoundingOfferActive" class="shrink-0 text-end leading-none">
                    <span class="mb-0.5 block text-[10px] font-medium text-gray-500 dark:text-gray-400" aria-hidden="true">{{ t('planner.founding_normal_label') }}</span>
                    <s class="text-[13px] font-medium text-gray-500 dark:text-gray-400" aria-hidden="true">{{ webStandardAnnualPriceDisplay }}</s>
                    <span class="sr-only">{{ t('planner.upgrade_page_founding_reference_price', { price: webStandardAnnualPriceDisplay }) }}</span>
                  </span>
                </div>
                <p class="mt-0.5 text-[12px] text-gray-500 dark:text-gray-400">
                  {{ isNativeMobile ? nativeAnnualMonthlyEquivalentDisplay : webAnnualPerMonthDisplay }}
                </p>
                <p v-if="annualTrialLine" class="mt-0.5 text-[12px] font-medium text-indigo-600 dark:text-indigo-400">
                  {{ annualTrialLine }}
                </p>
                <p
                    v-if="isNativeMobile ? nativeSavingsPercent !== null : webAnnualSavingDisplay !== ''"
                    class="mt-1.5 inline-flex items-center gap-1 text-[12px] font-semibold short:mt-1 short:leading-snug text-emerald-700 dark:text-emerald-400"
                >
                  <span class="text-emerald-500">✦</span>
                  <template v-if="isNativeMobile">
                    {{ t('planner.upgrade_page_store_savings_percent', { percent: nativeSavingsPercent }) }}
                  </template>
                  <template v-else>
                    {{ webAnnualSavingDisplay }}
                  </template>
                </p>
              </template>
            </div>
          </div>
        </button>

        <!-- Monthly card -->
        <button
            type="button"
            class="w-full rounded-2xl border-2 p-3.5 short:p-3 text-start transition-all active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            :class="selectedPlan === 'monthly'
              ? 'border-indigo-600 bg-indigo-50/80 shadow-md shadow-indigo-500/15 ring-2 ring-indigo-500/20 dark:border-indigo-400 dark:bg-indigo-500/15 dark:ring-indigo-400/20'
              : 'border-gray-200 bg-white/60 hover:border-indigo-300 dark:border-white/15 dark:bg-white/[0.04] dark:hover:border-indigo-500/60'"
            @click="selectedPlan = 'monthly'"
        >
          <div class="flex items-start gap-3">
            <span
                class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition-all"
                :class="selectedPlan === 'monthly'
                  ? 'bg-indigo-600 dark:bg-indigo-400'
                  : 'border-2 border-gray-300 dark:border-white/30'"
            >
              <Check v-if="selectedPlan === 'monthly'" class="h-3 w-3 text-white" stroke-width="3.5"/>
            </span>

            <div class="min-w-0 flex-1">
              <span class="text-sm font-bold text-gray-900 dark:text-gray-50">
                {{ t('planner.upgrade_page_plan_monthly_title') }}
              </span>
              <div class="mt-1.5 flex items-end justify-between gap-3 short:mt-1">
                  <span class="text-xl font-extrabold text-gray-800 dark:text-gray-100">
                    {{ isNativeMobile ? nativeMonthlyPriceDisplay : webMonthlyPriceDisplay }}
                  </span>
                  <span v-if="webFoundingOfferActive" class="shrink-0 text-end leading-none">
                    <span class="mb-0.5 block text-[10px] font-medium text-gray-500 dark:text-gray-400" aria-hidden="true">{{ t('planner.founding_normal_label') }}</span>
                    <s class="text-[13px] font-medium text-gray-500 dark:text-gray-400" aria-hidden="true">{{ webStandardMonthlyPriceDisplay }}</s>
                    <span class="sr-only">{{ t('planner.upgrade_page_founding_reference_price', { price: webStandardMonthlyPriceDisplay }) }}</span>
                  </span>
                </div>
              <p
                  v-if="webMonthlySavingDisplay !== ''"
                  class="mt-1 inline-flex items-center gap-1 text-[12px] font-semibold text-emerald-700 dark:text-emerald-400"
              >
                <span class="text-emerald-500">✦</span>
                {{ webMonthlySavingDisplay }}
              </p>
              <p v-if="monthlyTrialLine" class="mt-0.5 text-[12px] font-medium text-indigo-600 dark:text-indigo-400">
                {{ monthlyTrialLine }}
              </p>
            </div>
          </div>
        </button>
      </div>
      </div>
    </template>

    <!-- Sync retry banner. Outside the scroll region on purpose: it reports a
         purchase that went through but did not sync, which must stay visible
         rather than scroll away above the action. -->
    <div
        v-if="syncPending"
        class="mt-3 shrink-0 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/90 p-4 text-start dark:border-amber-500/40 dark:bg-amber-950/30"
    >
      <AlertTriangle class="mt-0.5 h-4.5 w-4.5 shrink-0 text-amber-600 dark:text-amber-400"/>
      <div class="min-w-0 flex-1">
        <p class="text-sm font-medium text-amber-900 dark:text-amber-100">
          {{ t('planner.upgrade_page_sync_after_purchase_error') }}
        </p>
        <button
            :disabled="syncRetryLoading"
            class="mt-3 w-full rounded-lg bg-amber-700 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-amber-600 dark:hover:bg-amber-500"
            type="button"
            @click="retryRevenueCatSync"
        >
          {{
            syncRetryLoading
              ? t('planner.upgrade_page_sync_retry_loading')
              : t('planner.upgrade_page_sync_retry')
          }}
        </button>
      </div>
    </div>

    <template v-if="!purchaseSuccessVisible && !(isNativeMobile && nativePricingError)">
      <!-- The panel's footer, not a bar floating over its own content. It is a
           sibling of the scroll region rather than a `sticky` child of it, so
           it sits flush on the panel edge with nothing peeking underneath and
           no second set of corners inside the first. -->
      <div>
      <div class="reveal picker-d2">
        <button
            :disabled="checkoutDisabled"
            class="upgrade-picker-cta mt-4 short:mt-3 w-full rounded-2xl py-3.5 text-[15px] font-bold text-white transition-transform active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60 disabled:active:scale-100 touch-manipulation"
            type="button"
            @click="goToCheckout"
        >
          <span class="relative z-10">{{ ctaLabel }}</span>
        </button>
      </div>

      <!-- Legal line -->
      <div class="reveal picker-d2 mt-2.5 space-y-2 text-center text-xs leading-relaxed text-gray-600 dark:text-gray-300">
        <p v-if="!isNativeMobile">
          <template v-if="webPricingReady">
            {{ webDisclosure }}
            <a
                :href="upgradeTermsUrl"
                class="font-medium text-indigo-700 underline decoration-indigo-700/35 underline-offset-2 hover:text-indigo-800 dark:text-indigo-300 dark:decoration-indigo-300/35 dark:hover:text-indigo-200"
                rel="noopener noreferrer"
                target="_blank"
                @click="openUpgradeTerms"
            >{{ t('terms_and_conditions') }}</a>.
          </template>
        </p>
        <p v-else-if="isIosPlatform">
          {{ t('planner.upgrade_page_trial_disclosure_ios') }}
          <a
              :href="APPLE_STANDARD_EULA_URL"
              class="font-medium text-indigo-700 underline decoration-indigo-700/35 underline-offset-2 dark:text-indigo-300 dark:decoration-indigo-300/35"
              rel="noopener noreferrer"
              target="_blank"
              @click.prevent="openExternalUrl(APPLE_STANDARD_EULA_URL)"
          >{{ t('planner.upgrade_page_apple_eula_link') }}</a>.
          <a
              :href="upgradeTermsUrl"
              class="font-medium text-indigo-700 underline decoration-indigo-700/35 underline-offset-2 dark:text-indigo-300 dark:decoration-indigo-300/35"
              rel="noopener noreferrer"
              target="_blank"
              @click="openUpgradeTerms"
          >{{ t('terms_and_conditions') }}</a>.
        </p>
        <p v-else>
          {{ t('planner.upgrade_page_trial_disclosure_android') }}
          <a
              :href="upgradeTermsUrl"
              class="font-medium text-indigo-700 underline decoration-indigo-700/35 underline-offset-2 dark:text-indigo-300 dark:decoration-indigo-300/35"
              rel="noopener noreferrer"
              target="_blank"
              @click="openUpgradeTerms"
          >{{ t('terms_and_conditions') }}</a>.
        </p>
        <p v-if="isNativeMobile && showNativeTrialNote">
          {{ t('planner.upgrade_page_native_trial_note') }}
        </p>
      </div>

      <p v-if="errorMessage" class="mt-3 text-center text-sm font-medium text-red-600 dark:text-red-400">
        {{ errorMessage }}
      </p>
      </div>
    </template>
  </div>
</template>

<script setup>
import { onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { AlertTriangle, Check, Crown } from 'lucide-vue-next'
import { APPLE_STANDARD_EULA_URL } from '@/constants/subscription'
import { useUpgradeCheckout } from '@/composables/useUpgradeCheckout'
import FoundingOfferNote from '@/components/upgrade/FoundingOfferNote.vue'
import WebPricingState from '@/components/upgrade/WebPricingState.vue'

const props = defineProps({
  source: {
    type: String,
    required: true,
  },
})

defineEmits(['continue'])

const { t } = useI18n()

const {
  selectedPlan,
  isNativeMobile,
  isIosPlatform,
  errorMessage,
  syncPending,
  syncRetryLoading,
  purchaseSuccessVisible,
  purchaseSuccessMessage,
  purchaseSuccessTitle,
  nativePackages,
  nativePricingError,
  nativePricingRetryLoading,
  nativeMonthlyPriceDisplay,
  nativeAnnualPriceDisplay,
  nativeAnnualMonthlyEquivalentDisplay,
  nativeSavingsPercent,
  webSelectedPlanPriceLabel,
  webFoundingOfferActive,
  foundingSeatsLeft,
  foundingSeatCap,
  webPricingReady,
  webPricingLoading,
  webPricingFailed,
  retryWebPricing,
  webDisclosure,
  webMonthlySavingDisplay,
  webMonthlyPriceDisplay,
  webAnnualPriceDisplay,
  webAnnualBilledDisplay,
  webAnnualPerMonthDisplay,
  webAnnualSavingDisplay,
  webStandardMonthlyPriceDisplay,
  webStandardAnnualPriceDisplay,
  annualTrialLine,
  monthlyTrialLine,
  showNativeTrialNote,
  checkoutDisabled,
  ctaLabel,
  loadNativeOfferings,
  retryLoadNativeOfferings,
  retryRevenueCatSync,
  goToCheckout,
  upgradeTermsUrl,
  openUpgradeTerms,
  openExternalUrl,
} = useUpgradeCheckout({ source: props.source })

const unlockedItems = [
  t('planner.upgrade_page_feature_1_title'),
  t('planner.upgrade_page_feature_2_title'),
  t('planner.upgrade_page_feature_3_title'),
]

const CONFETTI_COLOURS = ['#fbbf24', '#f472b6', '#a5b4fc', '#6ee7b7', '#f0abfc']
const confetti = Array.from({ length: 12 }, (_, i) => ({
  insetInlineStart: `${(i * 67) % 100}%`,
  backgroundColor: CONFETTI_COLOURS[i % CONFETTI_COLOURS.length],
  width: `${5 + (i % 3) * 2}px`,
  height: `${8 + (i % 4) * 2}px`,
  animationDelay: `${(i % 6) * 0.12}s`,
  animationDuration: `${1.8 + (i % 5) * 0.3}s`,
}))

onMounted(async () => {
  if (isNativeMobile.value) {
    await loadNativeOfferings()
  }
})
</script>

<style scoped>
/* Shared .reveal keyframe lives in src/assets/atmosphere.css. */
.picker-d1 { animation-delay: 0.05s; }
.picker-d2 { animation-delay: 0.14s; }

.upgrade-picker-cta {
  position: relative;
  overflow: hidden;
  background: linear-gradient(92deg, #4f46e5 0%, #9333ea 55%, #be185d 100%);
  box-shadow: 0 10px 30px -8px rgba(124, 58, 237, 0.55);
  animation: cta-glow 3s ease-in-out 1.2s infinite;
}

.upgrade-picker-cta::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(115deg, transparent 30%, rgba(255, 255, 255, 0.35) 50%, transparent 70%);
  transform: translateX(-120%);
  animation: sheen 3.5s ease-in-out 1.5s infinite;
}

.confetti {
  position: absolute;
  top: -5%;
  border-radius: 2px;
  opacity: 0;
  animation-name: confetti-fall;
  animation-timing-function: ease-in;
  animation-fill-mode: forwards;
}

@keyframes confetti-fall {
  0% { opacity: 1; transform: translateY(0) rotate(0deg); }
  85% { opacity: 1; }
  100% { opacity: 0; transform: translateY(220px) rotate(540deg); }
}

@media (prefers-reduced-motion: reduce) {
  .upgrade-picker-cta,
  .upgrade-picker-cta::after,
  .reveal {
    animation: none !important;
    opacity: 1;
  }

  .confetti { display: none; }
}
</style>
